import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CalendarDays, TrendingUp, TrendingDown, Award, AlertTriangle } from 'lucide-react';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const WEEKDAY_LABELS = ['M','T','W','T','F','S','S'];
const fmtCurrency = (v) =>
  `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function YearHeatmapCard({ BACKEND_URL }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/financials/year-heatmap?year=${year}`,
        { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [BACKEND_URL, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group days by month for rendering 12 mini-calendars
  const monthGrids = useMemo(() => {
    if (!data) return [];
    const byMonth = Array(12).fill(null).map(() => []);
    data.days.forEach((d) => byMonth[d.month - 1].push(d));
    return byMonth;
  }, [data]);

  const cellColor = (status, net) => {
    if (status === 'future') return 'bg-slate-50';
    if (status === 'empty') return 'bg-slate-200';
    if (status === 'profit') {
      // Intensity by relative amount (max $5k)
      const intensity = Math.min(1, (net || 0) / 5000);
      if (intensity > 0.75) return 'bg-emerald-600';
      if (intensity > 0.4) return 'bg-emerald-500';
      return 'bg-emerald-400';
    }
    if (status === 'near') return 'bg-amber-400';
    if (status === 'loss') {
      const intensity = Math.min(1, Math.abs(net || 0) / 5000);
      if (intensity > 0.75) return 'bg-red-600';
      if (intensity > 0.4) return 'bg-red-500';
      return 'bg-red-400';
    }
    return 'bg-slate-100';
  };

  if (loading && !data) {
    return (
      <Card className="p-6"><div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
      </div></Card>
    );
  }
  if (!data) return null;

  const ys = data.year_stats;
  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <Card className="p-6" data-testid="year-heatmap-card">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Year at a Glance — {year}
          </h3>
          <p className="text-sm text-slate-500">
            Every day coloured by net P&amp;L (after workshop + fixed expenses). Green = profit, Amber = near break-even, Red = loss, Grey = no entry.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[110px]" data-testid="year-heatmap-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Year stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="p-3 bg-slate-50 border rounded">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Entries</p>
          <p className="text-lg font-bold text-slate-900">{ys.total_entries}</p>
        </div>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
          <p className="text-[10px] text-emerald-600 uppercase tracking-wide">Profitable Days</p>
          <p className="text-lg font-bold text-emerald-900">{ys.profitable_days}</p>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-[10px] text-red-600 uppercase tracking-wide">Loss Days</p>
          <p className="text-lg font-bold text-red-900">{ys.loss_days}</p>
        </div>
        <div className={`p-3 border rounded ${ys.total_net >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${ys.total_net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Net P&L (year)</p>
          <div className="flex items-center gap-1">
            {ys.total_net >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
            <p className={`text-lg font-bold ${ys.total_net >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
              {ys.total_net >= 0 ? '+' : '−'}{fmtCurrency(Math.abs(ys.total_net))}
            </p>
          </div>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded">
          <p className="text-[10px] text-amber-600 uppercase tracking-wide">Best / Worst Day</p>
          <div className="flex gap-2 mt-0.5 flex-wrap">
            {ys.best_day && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><Award className="h-3 w-3" />{ys.best_day.date.slice(5)}: {fmtCurrency(ys.best_day.net)}</span>}
            {ys.worst_day && <span className="inline-flex items-center gap-1 text-[11px] text-red-700"><AlertTriangle className="h-3 w-3" />{ys.worst_day.date.slice(5)}: {fmtCurrency(ys.worst_day.net)}</span>}
          </div>
        </div>
      </div>

      {/* 12 mini-month calendars */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-4">
        {monthGrids.map((monthDays, idx) => {
          if (monthDays.length === 0) return null;
          // Build weeks (columns by weekday) — array of 7 rows (Mon-Sun), each with cells
          const firstDow = monthDays[0].weekday;
          const cells = [];
          for (let i = 0; i < firstDow; i++) cells.push(null);
          cells.push(...monthDays);
          while (cells.length % 7 !== 0) cells.push(null);
          const weeks = [];
          for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
          
          const monthStats = data.monthly_totals[idx];
          
          return (
            <div key={idx} className="border rounded p-2 bg-white" data-testid={`heatmap-month-${idx+1}`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold text-slate-700">{MONTH_NAMES[idx]}</p>
                {monthStats.entries > 0 && (
                  <p className={`text-[10px] font-medium ${monthStats.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {monthStats.net >= 0 ? '+' : '−'}{fmtCurrency(Math.abs(monthStats.net))}
                  </p>
                )}
              </div>
              {/* Weekday header */}
              <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                {WEEKDAY_LABELS.map((w, i) => (
                  <div key={i} className="text-[8px] text-center text-slate-400">{w}</div>
                ))}
              </div>
              {/* Weeks */}
              <div className="space-y-0.5">
                {weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-0.5">
                    {week.map((cell, ci) => {
                      if (!cell) return <div key={ci} className="h-3.5"></div>;
                      return (
                        <div
                          key={ci}
                          className={`h-3.5 rounded-sm ${cellColor(cell.status, cell.net)} hover:ring-2 hover:ring-blue-400 cursor-pointer transition-all`}
                          title={cell.has_entry
                            ? `${cell.date}: Net ${fmtCurrency(cell.net)} (break-even ${fmtCurrency(cell.break_even)})`
                            : cell.status === 'future' ? `${cell.date}: future` : `${cell.date}: no entry`}
                          data-testid={`heatmap-cell-${cell.date}`}
                        ></div>
                      );
                    })}
                  </div>
                ))}
              </div>
              {monthStats.entries > 0 && (
                <p className="text-[9px] text-slate-500 mt-1 text-center">
                  {monthStats.profitable_days}/{monthStats.entries} profitable
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-600 border-t pt-3">
        <span>Less</span>
        <div className="flex items-center gap-0.5">
          <div className="h-3 w-3 bg-slate-200 rounded-sm" title="No entry"></div>
          <div className="h-3 w-3 bg-red-600 rounded-sm" title="Large loss"></div>
          <div className="h-3 w-3 bg-red-400 rounded-sm" title="Small loss"></div>
          <div className="h-3 w-3 bg-amber-400 rounded-sm" title="Near break-even"></div>
          <div className="h-3 w-3 bg-emerald-400 rounded-sm" title="Small profit"></div>
          <div className="h-3 w-3 bg-emerald-600 rounded-sm" title="Large profit"></div>
        </div>
        <span>More</span>
        <span className="ml-auto text-slate-400">Hover a cell for exact date & net amount. Seasonal patterns become obvious at a glance.</span>
      </div>
    </Card>
  );
}
